import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Check, ChevronRight, Plus, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import {
  dailyPulse,
  type DailyPulseAnswerValue,
  type DailyPulseQuestion,
} from 'services/api';
import {
  publishDailyPulsePreferencesUpdated,
  type DailyPulseDraftAnswer,
} from 'utils/dailyCheckIn';

import './DailyCheckinCustomization.css';

interface DailyCheckinCustomizationProps {
  open?: boolean;
  fromDailyPulse?: boolean;
  draftAnswers?: DailyPulseDraftAnswer[];
  onClose?: () => void;
  onExited?: () => void;
}

const CUSTOMIZATION_FADE_OUT_MS = 200;
const QUESTION_PAGE_SIZE = 12;
const TOPICS = [
  { key: 'mindset', label: 'Mindset', description: 'Emotions, perspective, and resetting' },
  { key: 'connection', label: 'Connection', description: 'Relationships and communication' },
  { key: 'movement', label: 'Movement', description: 'Activity, exercise, and strength' },
  { key: 'sleep', label: 'Sleep', description: 'Rest, routines, and recovery' },
  { key: 'nutrition', label: 'Nutrition', description: 'Food, hydration, and fuel' },
  { key: 'focus', label: 'Focus', description: 'Attention, priorities, and time' },
  { key: 'money', label: 'Money', description: 'Spending and financial habits' },
  { key: 'other', label: 'Other', description: 'More questions to make your own' },
] as const;

const formatQuestionType = (question: DailyPulseQuestion) => ({
  rating: '1–10 rating', boolean: 'Yes / No', duration: 'Hours / minutes', time: 'Time of day',
  number: question.unit ? `Number · ${question.unit}` : 'Number',
}[question.response_type]);

const questionTopic = (question: DailyPulseQuestion) => TOPICS.find((topic) => topic.key === question.topic) || TOPICS[7];

const valueLabel = (value: DailyPulseAnswerValue) => (
  typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
);

const QuestionSwitch = ({
  question,
  enabled,
  exploring,
  disabled,
  onToggle,
}: {
  question: DailyPulseQuestion;
  enabled: boolean;
  exploring: boolean;
  disabled: boolean;
  onToggle: () => void;
}) => {
  const switchRef = useRef<HTMLButtonElement>(null);
  return (
    <div className={`daily-pulse-customize__question ${enabled ? 'daily-pulse-customize__question--selected' : ''}`}>
      <div className="daily-pulse-customize__question-copy">
        <span>{question.prompt}</span>
        <small>{questionTopic(question).label} · {formatQuestionType(question)}</small>
        {!exploring && !enabled ? <small className="daily-pulse-customize__pending">Will be removed</small> : null}
      </div>
      <div className="daily-pulse-customize__question-actions">
        {exploring ? <span className="daily-pulse-customize__added">{enabled ? <><Check size={13} aria-hidden="true" /> Added</> : null}</span> : null}
        {!exploring && !enabled ? (
          <button type="button" disabled={disabled} className="daily-pulse-customize__text-button" aria-label={`Undo removal: ${question.prompt}`} onClick={() => { switchRef.current?.focus(); onToggle(); }}>Undo</button>
        ) : null}
        <button
          ref={switchRef}
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${question.prompt} — ${enabled ? 'on. Turn off.' : 'off. Turn on.'}`}
          className={`daily-pulse-customize__switch ${enabled ? 'daily-pulse-customize__switch--on' : ''}`}
          onClick={onToggle}
          disabled={disabled}
        >
          <span aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const LoadingQuestions = () => (
  <div className="daily-pulse-customize__loading" aria-live="polite" aria-busy="true">
    {['62%', '48%', '70%', '54%', '66%'].map((width) => (
      <div className="daily-pulse-customize__skeleton-row" key={width}>
        <i style={{ width }} />
        <span />
      </div>
    ))}
    <p>Loading the question library…</p>
  </div>
);

export const DailyCheckinCustomization = ({
  open = true,
  fromDailyPulse = false,
  draftAnswers = [],
  onClose = () => undefined,
  onExited,
}: DailyCheckinCustomizationProps) => {
  const { isMobile } = useResponsiveLayout();
  const [rendered, setRendered] = useState(open);
  const onExitedRef = useRef(onExited);
  const [questions, setQuestions] = useState<DailyPulseQuestion[]>([]);
  const [initialKeys, setInitialKeys] = useState<string[]>([]);
  const [enabledKeys, setEnabledKeys] = useState<string[]>([]);
  const [sessionKeys, setSessionKeys] = useState<string[]>([]);
  const [view, setView] = useState('mine');
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(QUESTION_PAGE_SIZE);
  const bodyRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const topicBackRef = useRef<HTMLButtonElement>(null);
  const discoveryFocusRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorKind, setErrorKind] = useState<'load' | 'save' | null>(null);
  const [discardedAnswer, setDiscardedAnswer] = useState<DailyPulseDraftAnswer | null>(null);
  const pendingSaveRef = useRef<string[] | null>(null);

  useEffect(() => {
    onExitedRef.current = onExited;
  }, [onExited]);

  useEffect(() => {
    if (open) {
      setRendered(true);
      return undefined;
    }
    if (!rendered) return undefined;

    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finishExit = () => {
      setRendered(false);
      onExitedRef.current?.();
    };

    if (reducedMotion) {
      finishExit();
      return undefined;
    }

    const timer = window.setTimeout(finishExit, CUSTOMIZATION_FADE_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKind(null);
    try {
      const overview = await dailyPulse.getOverview();
      setQuestions(overview.questions);
      setInitialKeys(overview.enabled_question_keys);
      setEnabledKeys(overview.enabled_question_keys);
      setSessionKeys(overview.enabled_question_keys);
    } catch {
      setErrorKind('load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const enabledSet = useMemo(() => new Set(enabledKeys), [enabledKeys]);
  const selectedCount = questions.filter((question) => enabledSet.has(question.key)).length;
  const myQuestions = questions.filter((question) => sessionKeys.includes(question.key));
  const availableTopics = TOPICS.filter((item) => questions.some((question) => questionTopic(question).key === item.key));
  const searching = Boolean(search.trim());
  const matchingQuestions = questions.filter((question) => searching
    ? `${question.prompt} ${questionTopic(question).label}`.toLowerCase().includes(search.trim().toLowerCase())
    : topic === 'all' || questionTopic(question).key === topic);
  const exploreQuestions = matchingQuestions.slice(0, visibleLimit);
  const exploreTitle = searching ? 'Search results' : topic === 'all' ? 'All questions' : TOPICS.find((item) => item.key === topic)?.label;
  const dirty = initialKeys.length !== enabledKeys.length
    || initialKeys.some((key) => !enabledSet.has(key));

  useEffect(() => {
    if (view === 'explore' && discoveryFocusRef.current) {
      const timer = window.setTimeout(() => {
        (topic || search.trim() ? topicBackRef.current : searchInputRef.current)?.focus();
        discoveryFocusRef.current = false;
      }, 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [topic, search, view]);

  const leave = onClose;
  const changeView = (nextView: string) => {
    setView(nextView);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };
  const chooseTopic = (nextTopic: string) => {
    discoveryFocusRef.current = true;
    setTopic(nextTopic);
    setSearch('');
    setVisibleLimit(QUESTION_PAGE_SIZE);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };
  const toggleQuestion = (key: string) => {
    if (saving) return;
    setSessionKeys((keys) => keys.includes(key) ? keys : [...keys, key]);
    setEnabledKeys((keys) => keys.includes(key)
      ? keys.filter((item) => item !== key)
      : questions.filter((item) => keys.includes(item.key) || item.key === key).map((item) => item.key));
  };
  const questionRows = (items: DailyPulseQuestion[], exploring: boolean) => (
    <div className="daily-pulse-customize__questions">
      {items.map((question) => <QuestionSwitch key={question.key} question={question} enabled={enabledSet.has(question.key)} exploring={exploring} disabled={saving} onToggle={() => toggleQuestion(question.key)} />)}
    </div>
  );

  const persist = async (keys: string[]) => {
    setSaving(true);
    setErrorKind(null);
    try {
      await dailyPulse.savePreferences(keys);
      publishDailyPulsePreferencesUpdated(keys);
      onClose();
    } catch {
      setErrorKind('save');
      setSaving(false);
    }
  };

  const save = () => {
    if (!dirty || saving) return;
    const newlyDisabledAnswer = draftAnswers.find(
      (answer) => !enabledSet.has(answer.key) && initialKeys.includes(answer.key),
    );
    if (newlyDisabledAnswer) {
      pendingSaveRef.current = enabledKeys;
      setDiscardedAnswer(newlyDisabledAnswer);
      return;
    }
    void persist(enabledKeys);
  };

  const panel = (
    <Tabs value={view} onValueChange={changeView} className={`daily-pulse-customize ${isMobile ? 'daily-pulse-customize--mobile' : ''}`}>
      <header className="daily-pulse-customize__header">
        <div>
          {isMobile ? (
            <button type="button" className="daily-pulse-customize__back" onClick={leave}>
              <ArrowLeft size={14} /> {fromDailyPulse ? 'Back to Daily Pulse' : 'Back'}
            </button>
          ) : null}
          <h1>Customize Daily Pulse</h1>
          <p>Choose the questions you want to see each day. Nothing is required, and you can change this whenever.</p>
        </div>
        {!isMobile ? (
          <button type="button" className="daily-pulse-customize__close" aria-label="Close customization" onClick={leave}>
            <X size={15} />
          </button>
        ) : null}
      </header>

      {!loading && errorKind !== 'load' ? (
        <TabsList aria-label="Customize questions" className="daily-pulse-customize__tabs">
          <TabsTrigger value="mine">My questions · {selectedCount}</TabsTrigger>
          <TabsTrigger value="explore">Explore questions</TabsTrigger>
        </TabsList>
      ) : null}
      <main ref={bodyRef} className="daily-pulse-customize__body">
        {errorKind ? (
          <div className="daily-pulse-customize__notice daily-pulse-customize__notice--error" role="alert">
            <AlertCircle size={18} />
            <span>
              <strong>{errorKind === 'load' ? "We couldn't load your questions." : "We couldn't save your changes."}</strong>
              <small>{errorKind === 'load'
                ? 'Your Daily Pulse draft is still safe.'
                : 'Your selections are still here — try again, or cancel to keep what you had.'}</small>
            </span>
          </div>
        ) : null}
        {loading ? <LoadingQuestions /> : errorKind === 'load' ? null : (
          <>
            <TabsContent value="mine" className="daily-pulse-customize__tab-content">
              <section className="daily-pulse-customize__group">
                <div className="daily-pulse-customize__group-heading">
                  <h2>Your daily questions</h2>
                  <button type="button" className="daily-pulse-customize__add" onClick={() => { chooseTopic(''); changeView('explore'); }}><Plus size={16} aria-hidden="true" /> Add questions</button>
                </div>
                {questionRows(myQuestions, false)}
                {!selectedCount ? (
                  <div className="daily-pulse-customize__empty">
                    No questions turned on. Daily Pulse won't be offered automatically — you can still add a daily note from Home.
                  </div>
                ) : null}
              </section>
            </TabsContent>
            <TabsContent value="explore" className="daily-pulse-customize__tab-content">
              <section className="daily-pulse-customize__group">
                <div className="daily-pulse-customize__filters">
                  <label>
                    <span>Search all questions</span>
                    <input ref={searchInputRef} type="search" value={search} placeholder="Try sleep, energy, or focus" onChange={(event) => { setSearch(event.target.value); setVisibleLimit(QUESTION_PAGE_SIZE); }} />
                  </label>
                </div>
                {topic || searching ? (
                  <>
                    <button ref={topicBackRef} type="button" className="daily-pulse-customize__back" onClick={() => chooseTopic('')}><ArrowLeft size={14} aria-hidden="true" /> Back to topics</button>
                    <div className="daily-pulse-customize__group-heading">
                      <h2>{exploreTitle}</h2>
                      <span>{matchingQuestions.length} {matchingQuestions.length === 1 ? 'question' : 'questions'}</span>
                    </div>
                    {questionRows(exploreQuestions, true)}
                    {!matchingQuestions.length ? <div className="daily-pulse-customize__empty">No questions match. Try another search or browse the topics.</div> : null}
                    {matchingQuestions.length > visibleLimit ? (
                      <button type="button" className="daily-pulse-customize__secondary daily-pulse-customize__show-more" onClick={() => setVisibleLimit((limit) => limit + QUESTION_PAGE_SIZE)}>Show more questions ({matchingQuestions.length - visibleLimit} remaining)</button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <h2>What would you like to check in on?</h2>
                    <div className="daily-pulse-customize__topics">
                      {availableTopics.map((item) => {
                        const total = questions.filter((question) => questionTopic(question).key === item.key).length;
                        return (
                          <button type="button" key={item.key} className="daily-pulse-customize__topic" onClick={() => chooseTopic(item.key)}>
                            <span><strong>{item.label}</strong><small>{item.description}</small><small>{total} {total === 1 ? 'question' : 'questions'}</small></span>
                            <ChevronRight size={16} aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                    {!availableTopics.length ? <div className="daily-pulse-customize__empty">There are no questions available right now. You can still add a daily note from Home.</div> : (
                      <button type="button" className="daily-pulse-customize__text-button daily-pulse-customize__show-all" onClick={() => chooseTopic('all')}>Show all questions</button>
                    )}
                  </>
                )}
              </section>
            </TabsContent>
          </>
        )}
      </main>

      <footer className="daily-pulse-customize__footer">
        {!loading && errorKind !== 'load' ? (
          <div className="daily-pulse-customize__selection-summary">
            <span role="status" aria-live="polite" aria-atomic="true">{selectedCount} {selectedCount === 1 ? 'question' : 'questions'} selected</span>
            <small>{selectedCount > 8 ? 'Eight or fewer keeps it quick, but you can keep more.' : 'Aim for eight or fewer to keep your check-in quick.'}</small>
          </div>
        ) : null}
        <div className="daily-pulse-customize__footer-actions">
        {errorKind === 'load' ? (
          <button type="button" className="daily-pulse-customize__secondary" onClick={() => { void load(); }}>Try again</button>
        ) : null}
        <button type="button" className="daily-pulse-customize__secondary" onClick={leave}>Cancel</button>
        <button
          type="button"
          className="daily-pulse-customize__primary"
          disabled={loading || saving || !dirty || errorKind === 'load'}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        </div>
      </footer>

      {discardedAnswer ? (
        <div className="daily-pulse-customize__confirm-backdrop">
          <div className="daily-pulse-customize__confirm" role="alertdialog" aria-modal="true" aria-labelledby="disabled-answer-title">
            <span className="daily-pulse-customize__confirm-eyebrow">Unsaved answer</span>
            <h2 id="disabled-answer-title">Turn off a question you've answered?</h2>
            <p>“{discardedAnswer.prompt}” has an unsaved answer of <strong>{valueLabel(discardedAnswer.value)}</strong>. Saving these changes removes that answer. Everything else in your draft stays.</p>
            <div>
              <button type="button" className="daily-pulse-customize__secondary" onClick={() => { pendingSaveRef.current = null; setDiscardedAnswer(null); }}>
                Keep the question
              </button>
              <button type="button" className="daily-pulse-customize__primary" onClick={() => {
                const keys = pendingSaveRef.current || enabledKeys;
                pendingSaveRef.current = null;
                setDiscardedAnswer(null);
                void persist(keys);
              }}>
                Turn off and discard
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Tabs>
  );

  if (!rendered) return null;

  if (isMobile) {
    return (
      <div className={`daily-pulse-customize-page daily-pulse-customize-page--${open ? 'open' : 'closed'}`}>
        {panel}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) leave(); }}>
      <DialogContent
        hideCloseButton
        className="daily-pulse-customize-dialog"
        overlayClassName="daily-pulse-customize-overlay"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Customize Daily Pulse</DialogTitle>
          <DialogDescription>Choose the questions you want to see each day.</DialogDescription>
        </DialogHeader>
        {panel}
      </DialogContent>
    </Dialog>
  );
};

export default DailyCheckinCustomization;
